import {Autocomplete, CardContent} from "@mui/material";
import {BreadcrumbItem, BreadcrumbLink, Breadcrumbs, EditForm, TextField} from "@variocube/app-ui";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import React, {useMemo, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {Plan, Tenant} from "../../api/client";
import {usePlanApi} from "../../api/plan";
import {useSubscriptionApi} from "../../api/subscription";
import {useTenantApi} from "../../api/tenant";
import {useLocalization} from "@smalox/shared";
import {queryKeys} from "../../utils/queryKeys";
import {View, ViewHeader} from "../View";

export function SubscriptionCreate() {
	const {t} = useLocalization();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {createSubscription} = useSubscriptionApi();
	const {queryPlans} = usePlanApi();
	const {queryTenants} = useTenantApi();

	const [tenantId, setTenantId] = useState<string>("");
	const [planId, setPlanId] = useState<number | null>(null);
	const [startDate, setStartDate] = useState<string>(new Date().toISOString().split("T")[0]);

	const {data: tenantsResult} = useQuery(
		queryKeys.tenants({size: 1000}),
		() => queryTenants({size: 1000}),
	);
	const {data: plansResult} = useQuery(
		queryKeys.plans({size: 1000}),
		() => queryPlans({size: 1000}),
	);

	const tenants = useMemo(() => tenantsResult?.content ?? [], [tenantsResult]);
	const plans = useMemo(() => plansResult?.content ?? [], [plansResult]);

	const mutation = useMutation({
		mutationFn: () => createSubscription({tenantId, planId: planId!, startDate}),
		onSuccess: () => {
			queryClient.invalidateQueries(queryKeys.subscriptions.all());
			navigate("..");
		},
	});

	const handleSave = async () => {
		if (!tenantId || !planId) return;
		mutation.mutate();
	};

	return (
		<View>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to={".."}>
					{t("subscriptions.title")}
				</BreadcrumbLink>
				<BreadcrumbItem>{t("subscriptions.create.title")}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t("subscriptions.create.title")} />
			<EditForm onSave={handleSave} labels={t} loading={mutation.isLoading}>
				<CardContent>
					<Autocomplete
						options={tenants}
						getOptionLabel={(option: Tenant) => option.name || option.id}
						value={tenants.find((t: Tenant) => t.id === tenantId) || null}
						onChange={(_, value: Tenant | null) => setTenantId(value?.id ?? "")}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t("subscriptions.tenant")}
								required
								fullWidth
							/>
						)}
					/>
				</CardContent>
				<CardContent>
					<Autocomplete
						options={plans}
						getOptionLabel={(option: Plan) => option.name}
						value={plans.find((p: Plan) => p.id === planId) || null}
						onChange={(_, value: Plan | null) => setPlanId(value?.id ?? null)}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t("subscriptions.plan")}
								required
								fullWidth
							/>
						)}
					/>
				</CardContent>
				<CardContent>
					<TextField
						label={t("subscriptions.startDate")}
						type="date"
						value={startDate}
						onChange={setStartDate}
						fullWidth
						required
					/>
				</CardContent>
			</EditForm>
		</View>
	);
}
